import React, { useEffect, useMemo } from "react";
import { Info } from "lucide-react";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as dashboardActions from "src/redux/slices/dashboardSlice";
import * as tenantActions from "src/redux/slices/tenantSlice";
import * as transactionActions from "src/redux/slices/transactionSlice";
import Card from "src/components/common/card/Card";
import CardHeader from "src/components/common/card/CardHeader";
import TableV2 from "src/components/ui/TableV2";
import { Badge } from "src/components/ui/Badge";
import { currencyFormatter } from "src/utils/common";
import { formatDateTime } from "src/utils/date-time";
import { TenantPlanSnapshot } from "src/types/tenant.types";
import PlanUsageChart from "src/components/charts/PlanUsageChart";
import Popover from "src/components/ui/Popover";

const getActivePlan = (plans: TenantPlanSnapshot[] | undefined) => {
  if (!plans?.length) return null;
  const now = new Date();
  const sortedPlans = [...plans].sort((a, b) => {
    const aDate = a.planStartsAt || a.createdAt || "";
    const bDate = b.planStartsAt || b.createdAt || "";
    return new Date(bDate).getTime() - new Date(aDate).getTime();
  });
  return (
    sortedPlans.find((plan) =>
      plan.planEndsAt ? new Date(plan.planEndsAt) >= now : false
    ) || sortedPlans[0]
  );
};

const getPlanStatus = (planEndsAt?: string) => {
  if (!planEndsAt) return "active";
  return new Date(planEndsAt) < new Date() ? "expired" : "active";
};

const getDaysLeft = (planEndsAt?: string) => {
  if (!planEndsAt) return null;
  const diff = new Date(planEndsAt).getTime() - new Date().getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

const SuperAdminDashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const { superAdminMetric, superAdminPlanUsage } = useAppSelector(
    (state) => state.dashboard
  );
  const { tenantList } = useAppSelector((state) => state.tenant);
  const { transactionsList } = useAppSelector((state) => state.transaction);

  useEffect(() => {
    dispatch(dashboardActions.getSuperAdminDashboardMetricRequest());
    dispatch(dashboardActions.getSuperAdminPlanUsageRequest({ months: 6 }));
    dispatch(tenantActions.getTenantsListRequest({ page: 1, limit: 200 }));
    dispatch(transactionActions.getTransactionListRequest({ page: 1, limit: 8 }));
  }, [dispatch]);

  const tenants = tenantList?.data?.data || [];
  const metrics = superAdminMetric.data;
  const recentTransactions = transactionsList?.data?.data || [];

  const expiringSoon = useMemo(() => {
    return tenants
      .map((tenant) => {
        const activePlan = getActivePlan(tenant.plans);
        const daysLeft = getDaysLeft(activePlan?.planEndsAt);
        return {
          _id: tenant._id,
          tenantName: tenant.tenantName,
          planName:
            activePlan?.planName ||
            activePlan?.planCode ||
            activePlan?.planId ||
            "No Plan",
          planEndsAt: activePlan?.planEndsAt,
          daysLeft,
          status: getPlanStatus(activePlan?.planEndsAt),
        };
      })
      .filter((item) => item.daysLeft !== null && item.daysLeft <= 14 && item.daysLeft >= 0)
      .sort((a, b) => (a.daysLeft ?? 0) - (b.daysLeft ?? 0))
      .slice(0, 8);
  }, [tenants]);

  const kpiCards = [
    {
      label: "Total Tenants",
      value: metrics?.totalTenants ?? 0,
      description: "All tenants registered on the platform.",
    },
    {
      label: "Tenants with Trial Plan",
      value: metrics?.tenantsWithTrialPlan ?? 0,
      description: "Tenants currently on a trial plan.",
    },
    {
      label: "Tenants with Active Plan",
      value: metrics?.tenantsWithActivePlan ?? 0,
      description: "Tenants whose paid plan is currently valid.",
    },
    {
      label: "Tenants with Plan Expired",
      value: metrics?.tenantsWithExpiredPlan ?? 0,
      description: "Tenants without an active plan.",
    },
    {
      label: "Total Users",
      value: metrics?.totalUsers ?? 0,
      description: "All users across all tenants.",
    },
    {
      label: "Active Users",
      value: metrics?.activeUsers ?? 0,
      description: "Users marked active across all tenants.",
    },
    {
      label: "Total Revenue",
      value: currencyFormatter(metrics?.totalRevenue || 0),
      description: "Sum of successful platform transactions.",
    },
    {
      label: "Total Transactions",
      value: metrics?.totalTransactions ?? 0,
      description: "All payment/refund transactions recorded.",
    },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {kpiCards.map((card) => (
          <div
            key={card.label}
            className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
          >
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm text-gray-500">{card.label}</p>
              {card.description && (
                <Popover
                  openOnHover
                  trigger={
                    <button className="text-gray-400 hover:text-gray-600">
                      <Info size={14} />
                    </button>
                  }
                >
                  <div className="max-w-xs px-3 py-2 text-xs text-gray-600">
                    {card.description}
                  </div>
                </Popover>
              )}
            </div>
            <p className="mt-2 text-2xl font-semibold text-gray-900">
              {card.value}
            </p>
            <div className="mt-4 h-1 w-12 rounded-full bg-primary-200" />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Card>
          <CardHeader
            title="Plan Usage"
            description="Active tenants by plan over time"
          />
          {superAdminPlanUsage.loading ? (
            <p className="text-sm text-gray-500 mt-4">Loading plan usage...</p>
          ) : superAdminPlanUsage.data?.series?.length ? (
            <PlanUsageChart
              labels={superAdminPlanUsage.data.labels}
              series={superAdminPlanUsage.data.series}
            />
          ) : (
            <p className="text-sm text-gray-500 mt-4">No plan usage data.</p>
          )}
        </Card>

        <Card>
          <CardHeader
            title="Expiring Soon"
            description="Plans ending in the next 14 days"
          />
          <TableV2
            columns={[
              { key: "tenantName", header: "Tenant" },
              { key: "planName", header: "Plan" },
              {
                key: "daysLeft",
                header: "Days Left",
                align: "right",
                accessor: ({ row }) => (
                  <span className="font-medium">{row.daysLeft}</span>
                ),
              },
              {
                key: "planEndsAt",
                header: "Ends On",
                accessor: ({ row }) =>
                  formatDateTime(row.planEndsAt, "DD MMM YYYY"),
              },
            ]}
            data={expiringSoon}
          />
        </Card>

        <Card>
          <CardHeader
            title="Recent Transactions"
            description="Latest transactions across the platform"
          />
          <TableV2
            columns={[
              {
                key: "transactionId",
                header: "Transaction",
                accessor: ({ row }) => row?.transactionId || row?._id,
              },
              {
                key: "amount",
                header: "Amount",
                align: "right",
                accessor: ({ row }) => currencyFormatter(row?.amount || 0),
              },
              {
                key: "status",
                header: "Status",
                accessor: ({ row }) => {
                  const status = row?.status || "";
                  const color =
                    status === "success"
                      ? "green"
                      : status === "failed"
                      ? "red"
                      : "yellow";
                  return <Badge text={status} color={color} />;
                },
              },
              {
                key: "createdAt",
                header: "Created",
                accessor: ({ row }) =>
                  formatDateTime(row.createdAt, "DD MMM YYYY"),
              },
            ]}
            data={recentTransactions}
          />
        </Card>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
